/**
 * EF-DOCUMENT-PREVIEW-STANDARDISATION-V2
 * 
 * Shared Document Preview Modal Component
 * 
 * Used across the platform for consistent document preview experience:
 * - Admin (Crew Profile → Documents)
 * - Client Portal (Crew Documents)
 * - Intake Workspace
 * - Crew PWA
 * 
 * SUPPORTED PREVIEW TYPES:
 * - PDF: iframe with inline=true
 * - Images (JPG, PNG, WEBP, GIF, SVG): img tag
 * - Text/CSV: rendered text (optional)
 * 
 * NOT PREVIEWABLE (force download):
 * - DOC/DOCX, XLS/XLSX, ZIP
 * - Unknown types
 * 
 * Features:
 * - Format-based rendering (PDF=iframe, Image=img, Other=download)
 * - Safe fallback for unsupported types
 * - Token authentication for secure endpoints
 * - Responsive design
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Eye, Download, FileText, ExternalLink, FileImage, File, AlertTriangle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_TOKEN_KEY = "eventsflo_auth_token";

/**
 * Get the authentication token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: File type detection utilities
 */
const FILE_TYPE_CONFIG = {
  // Previewable as iframe
  pdf: { type: 'pdf', label: 'PDF Document', icon: FileText, previewable: true },
  
  // Previewable as image
  jpg: { type: 'image', label: 'JPEG Image', icon: FileImage, previewable: true },
  jpeg: { type: 'image', label: 'JPEG Image', icon: FileImage, previewable: true },
  png: { type: 'image', label: 'PNG Image', icon: FileImage, previewable: true },
  webp: { type: 'image', label: 'WebP Image', icon: FileImage, previewable: true },
  gif: { type: 'image', label: 'GIF Image', icon: FileImage, previewable: true },
  svg: { type: 'image', label: 'SVG Image', icon: FileImage, previewable: true },
  
  // Optional text preview
  txt: { type: 'text', label: 'Text File', icon: FileText, previewable: true },
  csv: { type: 'text', label: 'CSV File', icon: FileText, previewable: true },
  
  // Not previewable - force download
  doc: { type: 'document', label: 'Word Document', icon: File, previewable: false },
  docx: { type: 'document', label: 'Word Document', icon: File, previewable: false },
  xls: { type: 'spreadsheet', label: 'Excel Spreadsheet', icon: File, previewable: false },
  xlsx: { type: 'spreadsheet', label: 'Excel Spreadsheet', icon: File, previewable: false },
  zip: { type: 'archive', label: 'ZIP Archive', icon: File, previewable: false },
  rar: { type: 'archive', label: 'RAR Archive', icon: File, previewable: false },
};

/**
 * Get file extension from filename or mime type
 */
const getFileExtension = (filename, mimeType) => {
  // Try filename first
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext && FILE_TYPE_CONFIG[ext]) return ext;
  }
  
  // Fallback to mime type
  const mime = (mimeType || "").toLowerCase();
  if (mime.includes("pdf")) return "pdf";
  if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
  if (mime.includes("png")) return "png";
  if (mime.includes("webp")) return "webp";
  if (mime.includes("gif")) return "gif";
  if (mime.includes("svg")) return "svg";
  if (mime.includes("text/plain")) return "txt";
  if (mime.includes("text/csv") || mime.includes("csv")) return "csv";
  if (mime.includes("msword") || mime.includes("wordprocessingml")) return "docx";
  if (mime.includes("ms-excel") || mime.includes("spreadsheetml")) return "xlsx";
  if (mime.includes("zip")) return "zip";
  
  return null;
};

/**
 * Get file type configuration
 */
const getFileTypeConfig = (filename, mimeType) => {
  const ext = getFileExtension(filename, mimeType);
  return FILE_TYPE_CONFIG[ext] || { 
    type: 'unknown', 
    label: mimeType || 'Unknown File Type', 
    icon: File, 
    previewable: false 
  };
};

/**
 * Get the display type label from mime type (legacy support)
 */
const getMimeTypeLabel = (mimeType, filename) => {
  const config = getFileTypeConfig(filename, mimeType);
  return config.label;
};

/**
 * DocumentPreviewModal - Reusable document preview component
 * EF-DOCUMENT-PREVIEW-STANDARDISATION-V2
 * 
 * @param {Object} props
 * @param {Object} props.document - Document object with file_url, mime_type, original_filename
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {string} props.fallbackFileUrl - Fallback URL if document.file_url not available
 * @param {string} props.downloadEndpoint - Custom download endpoint (optional)
 */
export function DocumentPreviewModal({ 
  document, 
  open, 
  onOpenChange,
  fallbackFileUrl,
  downloadEndpoint
}) {
  const [textContent, setTextContent] = useState(null);
  const [loadingText, setLoadingText] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  
  // Get auth token for iframe/img authentication
  const token = getAuthToken();
  
  // Build file URL with token query parameter (must compute even if document is null for hooks)
  const baseUrl = document?.file_url 
    ? `${API_URL}${document.file_url}`
    : fallbackFileUrl || (document?.id ? `${API_URL}/api/crew/documents/${document.id}/file` : '');
  
  // EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Add inline=true for preview mode
  const previewUrl = token && baseUrl
    ? `${baseUrl}?token=${encodeURIComponent(token)}&inline=true` 
    : baseUrl ? `${baseUrl}?inline=true` : '';
  
  // Download URL (attachment disposition)
  const downloadUrl = token && baseUrl
    ? `${baseUrl}?token=${encodeURIComponent(token)}` 
    : baseUrl;
  
  // Get file type configuration
  const fileConfig = document 
    ? getFileTypeConfig(document.original_filename || document.filename, document.mime_type)
    : { type: 'unknown', label: 'Unknown', icon: File, previewable: false };
  const FileIcon = fileConfig.icon;
  
  // Load text content for txt/csv files - hooks must be before any early return
  useEffect(() => {
    if (open && document && fileConfig.type === 'text' && fileConfig.previewable && previewUrl) {
      setLoadingText(true);
      setTextContent(null);
      setPreviewError(false);
      
      fetch(previewUrl)
        .then(res => {
          if (!res.ok) throw new Error('Failed to load text');
          return res.text();
        })
        .then(text => {
          // Limit text preview to 50KB for performance
          if (text.length > 50000) {
            setTextContent(text.substring(0, 50000) + '\n\n... [Content truncated - Download to view full file]');
          } else {
            setTextContent(text);
          }
        })
        .catch(err => {
          console.error('Text preview failed:', err);
          setPreviewError(true);
        })
        .finally(() => setLoadingText(false));
    }
  }, [open, document, fileConfig.type, fileConfig.previewable, previewUrl]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setTextContent(null);
      setPreviewError(false);
    }
  }, [open]);
  
  // Early return AFTER all hooks
  if (!document) return null;
  
  // Render preview content based on file type
  const renderPreviewContent = () => {
    // If preview not supported, show fallback
    if (!fileConfig.previewable || previewError) {
      return (
        <div className="text-center py-12" data-testid="preview-not-available">
          <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-amber-500/70" />
          <p className="text-foreground font-medium mb-2">Preview not available for this file type</p>
          <p className="text-sm text-muted-foreground mb-6">{fileConfig.label}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="default" asChild data-testid="download-btn-fallback">
              <a href={downloadUrl} download={document.original_filename}>
                <Download className="h-4 w-4 mr-2" />
                Download File
              </a>
            </Button>
          </div>
        </div>
      );
    }
    
    // PDF preview
    if (fileConfig.type === 'pdf') {
      return (
        <iframe
          src={previewUrl}
          className="w-full h-[60vh] border-0 rounded"
          title={document.original_filename || "PDF Preview"}
          data-testid="pdf-preview-iframe"
        />
      );
    }
    
    // Image preview
    if (fileConfig.type === 'image') {
      return (
        <img
          src={previewUrl}
          alt={document.original_filename || "Document"}
          className="max-w-full max-h-[60vh] object-contain"
          data-testid="image-preview"
          onError={(e) => {
            console.error("Image load failed:", previewUrl);
            setPreviewError(true);
          }}
        />
      );
    }
    
    // Text/CSV preview
    if (fileConfig.type === 'text') {
      if (loadingText) {
        return (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        );
      }
      
      if (textContent) {
        return (
          <div className="w-full h-[60vh] overflow-auto bg-zinc-900 rounded p-4" data-testid="text-preview">
            <pre className="text-sm text-zinc-300 font-mono whitespace-pre-wrap break-words">
              {textContent}
            </pre>
          </div>
        );
      }
    }
    
    // Fallback
    return (
      <div className="text-center py-12">
        <FileIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">Loading preview...</p>
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {document.original_filename || document.filename || "Document Preview"}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <FileIcon className="h-4 w-4" />
            {getMimeTypeLabel(document.mime_type, document.original_filename)}
            {!fileConfig.previewable && (
              <span className="text-amber-500 text-xs ml-2">(Preview not supported)</span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 min-h-[60vh] flex items-center justify-center bg-zinc-950/50 rounded-lg overflow-hidden">
          {renderPreviewContent()}
        </div>
        
        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-preview-btn">
            Close
          </Button>
          <Button asChild data-testid="download-btn">
            <a 
              href={downloadUrl}
              download={document.original_filename}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentPreviewModal;
